import { useEffect, useMemo, useState } from "react";
import { KeyRound, RefreshCw, ShieldCheck, Save } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

function Metric({ label, value }) {
  return (
    <div className="bg-[var(--bg-secondary)] rounded-2xl px-4 py-3">
      <div className="text-[10px] tracking-[0.22em] font-semibold text-[var(--gold)]">{label.toUpperCase()}</div>
      <div className="font-display text-2xl font-bold text-[var(--text-primary)] mt-1">{value}</div>
    </div>
  );
}

export default function SecuritySettingsEditor() {
  const [users, setUsers] = useState([]);
  const [audit, setAudit] = useState([]);
  const [mfa, setMfa] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    target_user_id: "",
    current_password: "",
    new_password: "",
    confirm_password: "",
    totp_code: "",
  });

  const load = async () => {
    setLoading(true);
    try {
      const [usersRes, auditRes, mfaRes] = await Promise.all([
        api.get("/admin/security/users"),
        api.get("/admin/security/password-audit"),
        api.get("/admin/security/mfa"),
      ]);
      setUsers(usersRes.data || []);
      setAudit(auditRes.data || []);
      setMfa(mfaRes.data || null);
      setForm((prev) => ({
        ...prev,
        target_user_id: prev.target_user_id || usersRes.data?.[0]?.id || "",
      }));
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not load security settings.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const selectedUser = useMemo(
    () => users.find((user) => user.id === form.target_user_id) || users[0],
    [users, form.target_user_id],
  );

  const rotateMfa = async () => {
    try {
      const { data } = await api.post("/admin/security/mfa/rotate");
      setMfa(data);
      toast.success("Two-factor secret rotated.");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not rotate MFA secret.");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (form.new_password !== form.confirm_password) {
      toast.error("New password and confirmation do not match.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/admin/security/password", {
        current_password: form.current_password,
        new_password: form.new_password,
        totp_code: form.totp_code,
        target_user_id: form.target_user_id,
      });
      toast.success("Password updated successfully.");
      setForm((prev) => ({
        ...prev,
        current_password: "",
        new_password: "",
        confirm_password: "",
        totp_code: "",
      }));
      await load();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not change password.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-3xl border border-[var(--border-light)] p-10 text-sm text-[var(--text-secondary)]">
        Loading security settings...
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="security-settings-editor">
      <div>
        <div className="eyebrow mb-2">SECURITY</div>
        <h2 className="font-display text-2xl md:text-3xl font-bold text-[var(--text-primary)]">Password management and 2FA</h2>
        <p className="text-sm text-[var(--text-secondary)] mt-2 max-w-2xl">
          Password changes require the current password plus a 6-digit TOTP code. Every change is written to the audit log.
        </p>
      </div>

      <div className="grid md:grid-cols-3 gap-5">
        <Metric label="Admin Accounts" value={users.length} />
        <Metric label="MFA Enabled" value={users.filter((user) => user.mfa_enabled).length} />
        <Metric label="Password Events" value={audit.length} />
      </div>

      <div className="grid xl:grid-cols-[1fr_0.85fr] gap-6">
        <form onSubmit={submit} className="bg-white rounded-3xl border border-[var(--border-light)] p-6 space-y-5">
          <div className="flex items-center gap-2">
            <KeyRound className="w-4 h-4 text-[var(--gold)]" />
            <div className="font-display text-xl font-bold text-[var(--text-primary)]">Change Password</div>
          </div>

          <div>
            <label className="label-x">Target Admin</label>
            <select className="input-x" value={form.target_user_id} onChange={(e) => setForm((prev) => ({ ...prev, target_user_id: e.target.value }))} data-testid="security-target-user">
              {users.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.email}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="label-x">Current Password</label>
            <input type="password" className="input-x" value={form.current_password} onChange={(e) => setForm((prev) => ({ ...prev, current_password: e.target.value }))} data-testid="security-current-password" />
          </div>

          <div>
            <label className="label-x">New Password</label>
            <input type="password" className="input-x" value={form.new_password} onChange={(e) => setForm((prev) => ({ ...prev, new_password: e.target.value }))} data-testid="security-new-password" />
            <p className="text-xs text-[var(--text-secondary)] mt-2">Minimum 12 characters with uppercase, lowercase, number and special character.</p>
          </div>

          <div>
            <label className="label-x">Confirm New Password</label>
            <input type="password" className="input-x" value={form.confirm_password} onChange={(e) => setForm((prev) => ({ ...prev, confirm_password: e.target.value }))} data-testid="security-confirm-password" />
          </div>

          <div>
            <label className="label-x">2FA Code</label>
            <input type="text" inputMode="numeric" maxLength={6} className="input-x font-mono-tab tracking-[0.2em]" value={form.totp_code} onChange={(e) => setForm((prev) => ({ ...prev, totp_code: e.target.value.replace(/\D/g, "") }))} data-testid="security-totp-code" />
          </div>

          <button type="submit" className="btn-primary disabled:opacity-50" disabled={saving || !selectedUser} data-testid="security-password-save">
            <Save className="w-4 h-4" /> {saving ? "Saving..." : "Change Password"}
          </button>
        </form>

        <div className="space-y-6">
          <div className="bg-white rounded-3xl border border-[var(--border-light)] p-6">
            <div className="flex items-center justify-between gap-3 mb-4">
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-[var(--gold)]" />
                <div className="font-display text-xl font-bold text-[var(--text-primary)]">Authenticator Setup</div>
              </div>
              <button type="button" className="btn-secondary" onClick={rotateMfa} data-testid="security-rotate-mfa">
                <RefreshCw className="w-4 h-4" /> Rotate
              </button>
            </div>
            <div className="space-y-3 text-sm text-[var(--text-secondary)]">
              <p>Add the secret below to Google Authenticator, 1Password or any TOTP-compatible app.</p>
              <div className="bg-[var(--bg-secondary)] rounded-2xl p-4">
                <div className="label-x">Secret</div>
                <div className="font-mono-tab text-base text-[var(--text-primary)] break-all" data-testid="security-mfa-secret">{mfa?.secret}</div>
              </div>
              <div className="bg-[var(--bg-secondary)] rounded-2xl p-4">
                <div className="label-x">OTPAuth URI</div>
                <div className="font-mono-tab text-xs text-[var(--text-primary)] break-all">{mfa?.otpauth_uri}</div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-3xl border border-[var(--border-light)] p-6">
            <div className="font-display text-xl font-bold text-[var(--text-primary)] mb-4">Password Audit Log</div>
            <div className="space-y-3 max-h-[360px] overflow-y-auto">
              {audit.length === 0 ? (
                <div className="text-sm text-[var(--text-secondary)]">No password changes have been recorded yet.</div>
              ) : (
                audit.map((entry) => (
                  <div key={entry.id} className="rounded-2xl border border-[var(--border-light)] p-4">
                    <div className="text-sm font-semibold text-[var(--text-primary)]">{entry.target_email}</div>
                    <div className="text-xs text-[var(--text-secondary)] mt-1">
                      Changed by {entry.actor_email} on {new Date(entry.changed_at).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" })}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

